import { supabase } from '../lib/supabase';

const BUCKET_NAME = 'logos'; // reuse existing public bucket for slide images

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) return obj?.map(item => toCamelCase(item));
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      acc[camelKey] = toCamelCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) return obj?.map(item => toSnakeCase(item));
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
      acc[snakeKey] = toSnakeCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

export const slideService = {
  // Get all slides ordered by sort_order
  async getAll() {
    try {
      const { data, error } = await supabase
        ?.from('slide_configs')
        ?.select('*')
        ?.order('sort_order', { ascending: true });

      if (error) throw error;
      return { data: toCamelCase(data || []), error: null };
    } catch (error) {
      console.error('Error fetching slides:', error?.message);
      return { data: [], error };
    }
  },

  // Get only active slides for display
  async getActive() {
    try {
      const { data, error } = await supabase
        ?.from('slide_configs')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('sort_order', { ascending: true });

      if (error) throw error;
      return { data: toCamelCase(data || []), error: null };
    } catch (error) {
      console.error('Error fetching active slides:', error?.message);
      return { data: [], error };
    }
  },

  // Create a new slide
  async create(slideData) {
    try {
      const { data: userData } = await supabase?.auth?.getUser();
      const snakeData = toSnakeCase(slideData);
      const { data, error } = await supabase
        ?.from('slide_configs')
        ?.insert({ ...snakeData, created_by: userData?.user?.id })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error creating slide:', error?.message);
      return { data: null, error };
    }
  },

  // Update a slide
  async update(id, slideData) {
    try {
      const snakeData = toSnakeCase(slideData);
      const { data, error } = await supabase
        ?.from('slide_configs')
        ?.update(snakeData)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating slide:', error?.message);
      return { data: null, error };
    }
  },

  // Delete a slide
  async delete(id) {
    try {
      const { error } = await supabase
        ?.from('slide_configs')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting slide:', error?.message);
      return { error };
    }
  },

  // Duplicate a slide
  async duplicate(id, currentSlidesCount) {
    try {
      const { data: userData } = await supabase?.auth?.getUser();
      const { data: original, error: fetchError } = await supabase
        ?.from('slide_configs')
        ?.select('*')
        ?.eq('id', id)
        ?.single();

      if (fetchError) throw fetchError;

      const { id: _id, created_at: _ca, updated_at: _ua, created_by: _cb, ...rest } = original;

      const { data, error } = await supabase
        ?.from('slide_configs')
        ?.insert({
          ...rest,
          title: `Copy of ${original?.title || 'Untitled Slide'}`,
          sort_order: currentSlidesCount,
          created_by: userData?.user?.id
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error duplicating slide:', error?.message);
      return { data: null, error };
    }
  },

  // Reorder slides - update sort_order for multiple slides
  async reorder(slides) {
    try {
      const updates = slides?.map((slide, index) =>
        supabase
          ?.from('slide_configs')
          ?.update({ sort_order: index })
          ?.eq('id', slide?.id)
      );
      await Promise.all(updates);
      return { error: null };
    } catch (error) {
      console.error('Error reordering slides:', error?.message);
      return { error };
    }
  },

  // Upload image for a slide (uses logos bucket which is public)
  async uploadImage(file) {
    try {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml', 'image/gif'];
      if (!allowedTypes?.includes(file?.type)) {
        return { success: false, error: 'Invalid file type. Please upload JPG, PNG, WebP, SVG, or GIF.' };
      }
      if (file?.size > 10485760) {
        return { success: false, error: 'File size exceeds 10MB limit.' };
      }

      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `slide-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase?.storage
        ?.from(BUCKET_NAME)
        ?.upload(fileName, file, { cacheControl: '3600', upsert: true });

      if (uploadError) return { success: false, error: uploadError?.message };

      const { data: urlData } = supabase?.storage
        ?.from(BUCKET_NAME)
        ?.getPublicUrl(fileName);

      return { success: true, url: urlData?.publicUrl };
    } catch (error) {
      return { success: false, error: error?.message || 'Failed to upload image' };
    }
  },
};

export default slideService;
